import { useEffect, useRef, useCallback } from 'react'
import { checkPastStartGameRunning } from '../../Utils'
import { STALE_SEASON_EVENTS_INTERVAL_TIME } from '../../Constant/season'
import { logEvent } from '../../Utils/analytics/firAnalytics'
import { normalizeError } from '../../Utils'
import type { EventGQL } from '../../Api'

type UseGameCheckIntervalProps = {
  events: EventGQL[]
  fetchEvents: () => Promise<void>
}

export const useGameCheckInterval = ({
  events,
  fetchEvents,
}: UseGameCheckIntervalProps): void => {
  const gameCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef<boolean>(true)

  useEffect(() => {
    isMountedRef.current = true
    if (!events || events.length === 0 || gameCheckIntervalRef.current !== null) return
    runGameCheckInterval()

    return () => {
      isMountedRef.current = false
      clearAllIntervals()
    }
  }, [events])

  const clearAllIntervals = useCallback((): void => {
    if (gameCheckIntervalRef.current !== null) {
      clearInterval(gameCheckIntervalRef.current)
      gameCheckIntervalRef.current = null
    }
  }, [])

  // --- Fetching Logic ---

  const refetchSeasonEvents = useCallback(async (): Promise<void> => {
    if (!isMountedRef.current) return
    try {
      await fetchEvents()
    } catch (err) {
      handleError(err, 'fetchSeasonEvents')
    }
  }, [fetchEvents])

  /*
   * Interval to check if any events are past their start time, but still are set to 0
   * If so, fetch the latest events
   * Runs every 5 minutes
   */
  const runGameCheckInterval = useCallback((): void => {
    if (gameCheckIntervalRef.current !== null || !isMountedRef.current) return

    gameCheckIntervalRef.current = setInterval(() => {
      if (!isMountedRef.current) {
        clearAllIntervals()
        return
      }

      try {
        if (checkPastStartGameRunning(events)) {
          refetchSeasonEvents()
        }
      } catch (err) {
        handleError(err, 'runGameCheckInterval')
      }
    }, STALE_SEASON_EVENTS_INTERVAL_TIME)
  }, [clearAllIntervals, refetchSeasonEvents, events])

  const handleError = useCallback(
    (error: unknown, context: string, gameId?: string): void => {
      const normalizedError = normalizeError(error)
      console.error(`Error in ${context}:`, normalizedError)

      logEvent('live_game_error', {
        context,
        error_message: normalizedError.message,
        game_id: gameId || 'unknown',
      })
    },
    []
  )
}
